export function selectOptions(select, query = '') {
  const term = query.trim().toLocaleLowerCase();
  return Array.from(select.options, (option, index) => ({
    value: option.value, label: option.textContent.trim(), index, disabled: option.disabled,
  })).filter(option => !option.disabled && `${option.label} ${option.value}`.toLocaleLowerCase().includes(term));
}

export function selectNextIndex(key, current, length) {
  if (!length) return -1;
  if (key === 'Home') return 0;
  if (key === 'End') return length - 1;
  return Math.max(0, Math.min(length - 1, current + (key === 'ArrowUp' ? -1 : 1)));
}

// Keep the original select inside its native localization/app component's ref ownership.
class ScSelect extends HTMLElement {
  static sequence = 0;
  #native;
  #control;
  #trigger;
  #value;
  #popup;
  #search;
  #list;
  #empty;
  #rows = [];
  #cursor = 0;
  #signature = '';
  #abort;
  #controlAbort;
  #observer = new MutationObserver(() => this.#sync());

  connectedCallback() {
    this.#abort = new AbortController();
    const options = { signal:this.#abort.signal };
    this.addEventListener('change', () => this.#sync(), options);
    document.addEventListener('BUCKS_SELECTED_CURRENCY', event => {
      if (this.dataset.nativeSelector === '#bucksSelector' && typeof event.detail === 'string') this.#sync(event.detail);
    }, options);
    this.addEventListener('keydown', event => this.#key(event), options);
    this.addEventListener('input', event => {
      if (event.target === this.#search) this.#render();
    }, options);
    this.addEventListener('click', event => {
      const row = event.target instanceof Element ? event.target.closest('[data-select-value]') : null;
      if (row && this.#list.contains(row)) {
        event.stopPropagation();
        this.#choose(row.dataset.selectValue);
      }
    }, options);
    this.#observer.observe(this, { childList:true, subtree:true, attributes:true, attributeFilter:['selected','disabled'] });
    this.#sync();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#controlAbort?.abort();
    this.#observer.disconnect();
    this.#control?.remove();
    this.removeAttribute('data-enhanced');
    if (this.#native) {
      this.#native.removeAttribute('aria-hidden');
      this.#native.removeAttribute('tabindex');
    }
    this.#native = undefined;
    this.#control = undefined;
    this.#signature = '';
  }

  #sync(preferredCurrency) {
    const select = this.querySelector(this.dataset.nativeSelector || 'select');
    if (!(select instanceof HTMLSelectElement)) return;
    if (this.dataset.nativeSelector === '#bucksSelector') {
      const currency = preferredCurrency || this.querySelector('.bucks-selected')?.textContent.trim();
      if (currency && Array.from(select.options).some(option => option.value === currency)) select.value = currency;
    }
    const signature = JSON.stringify([select.value, select.disabled, Array.from(select.options, option => [option.value, option.textContent, option.disabled])]);
    if (select === this.#native && signature === this.#signature && this.#control?.parentElement === this && this.hasAttribute('data-enhanced')) return;
    this.#signature = signature;
    this.#native = select;
    select.setAttribute('aria-hidden', 'true');
    select.tabIndex = -1;
    if (!this.#control || this.#control.parentElement !== this) this.#build();
    this.#value.textContent = select.selectedOptions[0]?.textContent || this.dataset.label;
    this.#trigger.setAttribute('aria-label', `${this.dataset.label}: ${this.#value.textContent}`);
    this.#trigger.disabled = select.disabled;
    this.#render();
    this.setAttribute('data-enhanced', '');
  }

  focus(options) {
    this.#sync();
    (this.#trigger || this.#native)?.focus(options);
  }

  #build() {
    this.#controlAbort?.abort();
    this.#controlAbort = new AbortController();
    const id = `sc-select-${++ScSelect.sequence}`;
    this.#control = document.createElement('anchored-popover-component');
    this.#control.className = 'sc-select__control';
    this.#control.style.setProperty('--sc-select-anchor', `--${id}`);
    this.#trigger = document.createElement('button');
    this.#trigger.type = 'button';
    this.#trigger.className = 'sc-select__trigger';
    this.#trigger.setAttribute('ref','trigger');
    this.#trigger.setAttribute('aria-haspopup','listbox');
    this.#trigger.setAttribute('aria-expanded','false');
    this.#trigger.setAttribute('aria-controls',`${id}-list`);
    this.#trigger.setAttribute('popovertarget',id);
    this.#value = document.createElement('span');
    const caret = document.createElement('span');
    caret.className = 'sc-select__caret';
    caret.setAttribute('aria-hidden','true');
    this.#trigger.append(this.#value,caret);
    this.#popup = document.createElement('div');
    this.#popup.id = id;
    this.#popup.className = 'sc-select__popover';
    this.#popup.setAttribute('ref','popover');
    this.#popup.setAttribute('popover','auto');
    this.#search = document.createElement('input');
    this.#search.type = 'search';
    this.#search.className = 'sc-select__search';
    this.#search.placeholder = this.dataset.searchLabel;
    this.#search.setAttribute('aria-label',this.dataset.searchLabel);
    this.#search.setAttribute('aria-controls',`${id}-list`);
    this.#list = document.createElement('ul');
    this.#list.id = `${id}-list`;
    this.#list.className = 'sc-select__list';
    this.#list.setAttribute('role','listbox');
    this.#list.setAttribute('aria-label',this.dataset.label);
    this.#list.tabIndex = 0;
    this.#empty = document.createElement('p');
    this.#empty.className = 'sc-select__empty';
    this.#empty.setAttribute('role','status');
    this.#empty.textContent = this.dataset.emptyLabel;
    this.#popup.append(this.#search,this.#list,this.#empty);
    this.#control.append(this.#trigger,this.#popup);
    const options = { signal:this.#controlAbort.signal };
    this.#popup.addEventListener('beforetoggle', event => {
      const opening = event.newState === 'open';
      this.#trigger.setAttribute('aria-expanded',String(opening));
      if (opening) {
        const rect = this.#trigger.getBoundingClientRect();
        const above = rect.top - 16;
        const below = window.innerHeight - rect.bottom - 16;
        this.#popup.dataset.placement = above >= below ? 'above' : 'below';
        this.#popup.style.setProperty('--sc-select-height',`${Math.min(360,Math.max(120,Math.max(above,below)-8))}px`);
        this.#popup.style.setProperty('--sc-select-width',`${Math.max(220,rect.width)}px`);
        this.#search.value = '';
        this.#render();
      }
    }, options);
    this.#popup.addEventListener('toggle', event => {
      if (event.newState === 'open') {
        (this.#search.hidden ? this.#list : this.#search).focus();
        this.#activate(this.#cursor);
      }
    }, options);
    this.append(this.#control);
  }

  #render() {
    if (!this.#list) return;
    this.#search.hidden = this.#native.options.length <= 8;
    const records = selectOptions(this.#native,this.#search.value);
    this.#rows = records.map(option => {
      const row = document.createElement('li');
      row.id = `${this.#list.id}-${option.index}`;
      row.setAttribute('role','option');
      row.setAttribute('aria-selected',String(option.value === this.#native.value));
      row.dataset.selectValue = option.value;
      row.textContent = option.label;
      return row;
    });
    this.#list.replaceChildren(...this.#rows);
    this.#empty.hidden = records.length > 0;
    const selected = records.findIndex(option => option.value === this.#native.value);
    this.#activate(Math.max(0,selected));
  }

  #activate(index) {
    this.#cursor = index;
    this.#rows.forEach((row,i) => row.classList.toggle('is-active', i === index));
    const row = this.#rows[index];
    if (row) {
      this.#list.setAttribute('aria-activedescendant',row.id);
      if (this.#popup.matches(':popover-open')) row.scrollIntoView({block:'nearest'});
    } else this.#list.removeAttribute('aria-activedescendant');
  }

  #choose(value) {
    if (!Array.from(this.#native.options).some(option => option.value === value && !option.disabled)) return;
    this.#native.value = value;
    this.#close();
    this.#native.dispatchEvent(new Event('change',{bubbles:true}));
    this.#sync(this.dataset.nativeSelector === '#bucksSelector' ? value : undefined);
  }

  #close() {
    this.#popup.hidePopover();
    this.#trigger.focus();
  }

  #key(event) {
    if (event.target === this.#trigger) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        this.#popup.showPopover();
      }
      return;
    }
    if (event.target !== this.#list && event.target !== this.#search) return;
    if (event.key === 'Escape' || event.key === 'Tab') {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
      this.#close();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.#list.focus();
      this.#activate(selectNextIndex(event.key,this.#cursor,this.#rows.length));
    } else if (event.target === this.#list) {
      if (event.key === 'Home' || event.key === 'End') {
        event.preventDefault();
        this.#activate(selectNextIndex(event.key,this.#cursor,this.#rows.length));
      } else if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        const row = this.#rows[this.#cursor];
        if (row) this.#choose(row.dataset.selectValue);
      } else if (event.key.length === 1 && !this.#search.hidden) {
        event.preventDefault();
        this.#search.value = event.key;
        this.#render();
        this.#search.focus();
      }
    } else if (event.key === 'Enter') {
      event.preventDefault();
      const row = this.#rows[this.#cursor];
      if (row) this.#choose(row.dataset.selectValue);
    }
  }
}

if (!customElements.get('sc-select')) customElements.define('sc-select',ScSelect);
